import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Cable, Boxes, RefreshCw, TriangleAlert, Loader2, Plug, Activity } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, Badge, RUN_STYLE, fmtDate } from "./shared";

function Stat({ label, value, icon: Icon, to, testid }) {
  const body = (
    <div className="bg-card border border-border rounded-xl p-4 hover:border-primary/40 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="text-2xl font-bold text-foreground mt-2">{value}</p>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function IntegrationDashboard() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    integrationService.dashboard(params)
      .then(setData)
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="integration-dashboard-page">
      <PageHeader icon={Cable} title="Integration Hub"
        subtitle="Connector-ready fabric — every source maps to canonical records, safely and audited.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading && !data ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Stat label="Integrations" value={data.integrations} icon={Plug} to="/admin/integrations/list" testid="stat-integrations" />
            <Stat label="Sync Jobs" value={data.syncJobs} icon={RefreshCw} to="/admin/integrations/sync-jobs" testid="stat-jobs" />
            <Stat label="Total Runs" value={data.totalRuns} icon={Activity} to="/admin/integrations/sync-logs" testid="stat-runs" />
            <Stat label="Open Errors" value={data.openErrors} icon={TriangleAlert} to="/admin/integrations/errors" testid="stat-errors" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-xl p-5" data-testid="dash-by-type">
              <h2 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" /> By connector type</h2>
              {Object.keys(data.byType || {}).length === 0 ? <p className="text-xs text-muted-foreground">No integrations yet.</p> : (
                <ul className="space-y-2">
                  {Object.entries(data.byType).map(([t, n]) => (
                    <li key={t} className="flex items-center justify-between text-sm">
                      <span className="capitalize text-muted-foreground">{t}</span>
                      <span className="font-bold text-foreground">{n}</span>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/admin/integrations/connectors" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline mt-3"><Plug className="h-3.5 w-3.5" /> Browse connectors</Link>
            </div>

            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5" data-testid="dash-recent-runs">
              <h2 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /> Recent sync runs</h2>
              {(data.recentRuns || []).length === 0 ? <p className="text-xs text-muted-foreground">No sync runs yet.</p> : (
                <div className="space-y-2">
                  {data.recentRuns.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 border-b border-border pb-2 last:border-0" data-testid={`dash-run-${r.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{r.targetEntity} <span className="text-muted-foreground">· {r.connectorCode}</span></p>
                        <p className="text-[11px] text-muted-foreground">{fmtDate(r.startedAt)} · {r.counts?.created || 0} created / {r.counts?.failed || 0} failed</p>
                      </div>
                      <Badge label={r.status} styleMap={RUN_STYLE} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
